import { create } from "zustand";
import { persist } from "zustand/middleware";

const API_BASE = (import.meta.env.VITE_API_BASE_URL ??
  (import.meta.env.PROD ? "https://kkks-admin-app.vercel.app" : "http://localhost:3000")) as string;

/**
 * Fire-and-forget server logout. Backend is a no-op in v1 but will denylist
 * tokens in Phase 2; wiring it now means clients work either way.
 */
function notifyServerLogout(accessToken: string | null, refreshToken: string | null): void {
  if (!accessToken && !refreshToken) return;
  void fetch(`${API_BASE}/api/v1/auth/logout`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({ refreshToken }),
    keepalive: true,
  }).catch(() => { /* best effort */ });
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  staff: { id: string; name: string; role: string; permissions: string[]; kopitiamId: string } | null;
  setTokens: (access: string, refresh: string) => void;
  setStaff: (staff: AuthState["staff"]) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      staff: null,
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      setStaff: (staff) => set({ staff }),
      logout: () => {
        const { accessToken, refreshToken } = get();
        notifyServerLogout(accessToken, refreshToken);
        set({ accessToken: null, refreshToken: null, staff: null });
      },
    }),
    { name: "kawkaw.admin.auth.v1" },
  ),
);
